import { useState, useEffect, useRef } from 'react'
import { Button } from 'ui/components/Button'
import { ModalButtons } from 'ui/Modal'
import { Spinner } from 'ui/components/Spinner'
import { Notification, useNotificationContext } from 'ui/components/Notification'
import {
  useCancelTerminalRequest,
  useCreateIdentityRequest,
  useTerminalRequest,
} from 'api/TerminalRequests'
import { Trans, useTranslation } from 'react-i18next'
import { TerminalRequestStatusEnum, PaymentTerminal, TerminalRequest } from '@trybeapp/sdk'

interface WaitingForIdentityProps {
  terminal: PaymentTerminal
  onIdentityReceived: (terminalRequest: TerminalRequest) => void
  onCancel: () => void
}

export const WaitingForIdentity = ({
  terminal,
  onIdentityReceived,
  onCancel,
}: WaitingForIdentityProps) => {
  const { t } = useTranslation()
  const notification = useNotificationContext()
  const { mutateAsync: createIdentityRequest, isLoading: isStarting } = useCreateIdentityRequest()
  const { mutate: cancelRequest } = useCancelTerminalRequest()
  const [requestId, setRequestId] = useState<string | null>(null)
  const hasNotifiedRef = useRef(false)

  useEffect(() => {
    if (terminal && !requestId && !isStarting) {
      createIdentityRequest(
        {
          terminal_id: terminal.id,
        },
        {
          onSuccess: (response) => setRequestId(response.data.id),
          onError: (response: Response) => {
            notification.notifyApiError(response)
            onCancel()
          },
        }
      )
    }
  }, [terminal, requestId, createIdentityRequest, notification, onCancel, isStarting])

  const { data: { data: terminalRequest } = {} } = useTerminalRequest(requestId || '', {
    enabled: !!requestId,
  })

  useEffect(() => {
    if (terminalRequest?.status && !hasNotifiedRef.current) {
      const status = terminalRequest.status

      if (status === TerminalRequestStatusEnum.Succeeded) {
        notification.notify(
          <Notification
            title={t('global_header.identity_request.success_notification.title')}
            description={t('global_header.identity_request.success_notification.description')}
            variant="success"
            autoDismiss
          />
        )
        hasNotifiedRef.current = true

        onIdentityReceived(terminalRequest)
      } else if (status === TerminalRequestStatusEnum.Failed) {
        notification.notify(
          <Notification
            title={t('global_header.identity_request.failed_notification.title')}
            description={
              terminalRequest.processor_data?.error_message ||
              t('global_header.identity_request.failed_notification.description')
            }
            variant="danger"
          />
        )
        hasNotifiedRef.current = true
        onCancel()
      }
    }
  }, [terminalRequest, onIdentityReceived, notification, onCancel, t])

  const handleCancelRequest = () => {
    if (requestId) {
      cancelRequest(requestId, {
        onSuccess: () => {
          notification.notify(
            <Notification
              title={t('global_header.identity_request.cancelled_notification.title')}
              description={t('global_header.identity_request.cancelled_notification.description')}
              variant="info"
              autoDismiss
            />
          )
        },
      })
    }
    onCancel()
  }

  if (isStarting) {
    return (
      <div className="text-center py-8">
        <Spinner />
        <div className="mt-2 font-medium">
          {t('global_header.identity_request.is_starting.description_line_first')}
        </div>
        <div className="text-sm text-gray-500 mt-2">
          <Trans
            i18nKey="global_header.identity_request.is_starting.description_line_second"
            values={{ terminalNameOrId: terminal.name || terminal.id }}
            components={[<span className="font-semibold" />]}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="text-center py-8">
      <Spinner />
      <div className="mt-2 font-medium">
        {t('global_header.identity_request.waiting.description_line_first')}
      </div>
      <div className="text-sm text-gray-500 mt-2">
        <Trans
          i18nKey="global_header.identity_request.waiting.description_line_second"
          values={{ terminalNameOrId: terminal.name || terminal.id }}
          components={[<span className="font-semibold" />]}
        />
      </div>

      <ModalButtons>
        <Button label={t('modal_button.cancel')} variant="primary" onClick={handleCancelRequest} />
      </ModalButtons>
    </div>
  )
}
